import type {
  BulkRecordAttendanceRequest,
  SessionAttendance,
  UpdateAttendanceRequest,
} from '../../api/attendance.service';
import type {
  CancelSessionRequest,
  ClassSession,
  CreateAdHocSessionRequest,
  OverrideSessionRequest,
  RescheduleSessionRequest,
} from '../../api/class-session.service';
import type {
  AddStudentToSessionRequest,
  AddStudentToSessionResponse,
} from '../../api/class-session-students.service';
import type { GroupEnrollment } from '../../api/group-enrollment.service';
import type { TenantAccountRole } from '../../auth/tenant-auth.service';
import { memberFullName, type DemoMember, type DemoStoreData } from '../store-types';
import { route, type HandlerEntry } from '../handler-registry';

function inRange(date: string, from?: string | null, to?: string | null): boolean {
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}

function newSessionId(data: DemoStoreData): string {
  return `sess-adhoc-${data.sessions.length + 1}-${Date.now().toString(36).slice(-4)}`;
}

function newAttendanceId(sessionId: string, studentId: string): string {
  return `att-${sessionId}-${studentId}-${Date.now().toString(36).slice(-4)}`;
}

function newMemberId(data: DemoStoreData): string {
  return `athlete-guest-${data.members.length + 1}-${Date.now().toString(36).slice(-4)}`;
}

function newEnrollmentId(data: DemoStoreData): string {
  return `enr-guest-${data.enrollments.length + 1}-${Date.now().toString(36).slice(-4)}`;
}

export const sessionsHandlers: readonly HandlerEntry[] = [
  route('GET', /^\/api\/tenants\/([^/]+)\/class-sessions$/, (ctx, data) => {
    const from = ctx.query.get('from');
    const to = ctx.query.get('to');
    const locationId = ctx.query.get('locationId');
    const groupId = ctx.query.get('groupId');
    const coachId = ctx.query.get('coachId');
    return data.sessions.filter((s) => {
      if (!inRange(s.date, from, to)) return false;
      if (locationId && s.locationId !== locationId) return false;
      if (groupId && s.groupId !== groupId) return false;
      if (coachId && !s.coachTenantAccountIds.includes(coachId)) return false;
      return true;
    });
  }),
  route('GET', /^\/api\/tenants\/([^/]+)\/class-sessions\/([^/]+)$/, (ctx, data) => {
    const id = decodeURIComponent(ctx.match[2]);
    const found = data.sessions.find((s) => s.sessionId === id);
    return found ?? { error: { status: 404, message: 'Not found' } };
  }),
  route('POST', /^\/api\/tenants\/([^/]+)\/class-sessions$/, (ctx) => {
    const body = ctx.body as CreateAdHocSessionRequest;
    return {
      mutate: (data: DemoStoreData) => {
        const session: ClassSession = {
          sessionId: newSessionId(data),
          tenantId: data.tenant.tenantId,
          classScheduleId: null,
          groupId: body.groupId ?? null,
          locationId: body.locationId,
          coachTenantAccountIds: body.coachTenantAccountIds ?? [],
          date: body.date,
          startTime: body.startTime,
          endTime: body.endTime,
          status: 'Scheduled',
          type: body.type ?? 'Special',
          title: body.title ?? null,
          notes: body.notes ?? null,
          canceledAt: null,
          cancelReason: null,
          createdAt: new Date().toISOString(),
          updatedAt: null,
        };
        return {
          next: { ...data, sessions: [...data.sessions, session] },
          result: session,
        };
      },
    };
  }),
  route('PATCH', /^\/api\/tenants\/([^/]+)\/class-sessions\/([^/]+)$/, (ctx) => {
    const id = decodeURIComponent(ctx.match[2]);
    const body = ctx.body as OverrideSessionRequest;
    return {
      mutate: (data: DemoStoreData) => {
        const sessions = data.sessions.map((s) =>
          s.sessionId === id
            ? {
                ...s,
                coachTenantAccountIds:
                  body.coachTenantAccountIds ?? s.coachTenantAccountIds,
                startTime: body.startTime ?? s.startTime,
                endTime: body.endTime ?? s.endTime,
                title: body.title === undefined ? s.title : body.title,
                notes: body.notes === undefined ? s.notes : body.notes,
                updatedAt: new Date().toISOString(),
              }
            : s,
        );
        const result = sessions.find((s) => s.sessionId === id)!;
        return { next: { ...data, sessions }, result };
      },
    };
  }),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/class-sessions\/([^/]+)\/cancel$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as CancelSessionRequest;
      return {
        mutate: (data: DemoStoreData) => {
          const sessions = data.sessions.map((s) =>
            s.sessionId === id
              ? {
                  ...s,
                  status: 'Canceled' as const,
                  canceledAt: new Date().toISOString(),
                  cancelReason: body?.reason ?? null,
                  updatedAt: new Date().toISOString(),
                }
              : s,
          );
          const result = sessions.find((s) => s.sessionId === id)!;
          return { next: { ...data, sessions }, result };
        },
      };
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/class-sessions\/([^/]+)\/restore$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      return {
        mutate: (data: DemoStoreData) => {
          const sessions = data.sessions.map((s) =>
            s.sessionId === id
              ? {
                  ...s,
                  status: 'Scheduled' as const,
                  canceledAt: null,
                  cancelReason: null,
                  updatedAt: new Date().toISOString(),
                }
              : s,
          );
          const result = sessions.find((s) => s.sessionId === id)!;
          return { next: { ...data, sessions }, result };
        },
      };
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/class-sessions\/([^/]+)\/reschedule$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as RescheduleSessionRequest;
      return {
        mutate: (data: DemoStoreData) => {
          const sessions = data.sessions.map((s) =>
            s.sessionId === id
              ? {
                  ...s,
                  date: body.newDate,
                  startTime: body.newStartTime,
                  endTime: body.newEndTime,
                  notes: body.notes === undefined ? s.notes : body.notes,
                  updatedAt: new Date().toISOString(),
                }
              : s,
          );
          const result = sessions.find((s) => s.sessionId === id)!;
          return { next: { ...data, sessions }, result };
        },
      };
    },
  ),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/class-sessions\/([^/]+)\/attendance$/,
    (ctx, data) => {
      const sessionId = decodeURIComponent(ctx.match[2]);
      return data.attendances.filter((a) => a.classSessionId === sessionId);
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/class-sessions\/([^/]+)\/attendance$/,
    (ctx) => {
      const sessionId = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as BulkRecordAttendanceRequest;
      return {
        mutate: (data: DemoStoreData) => {
          const existing = data.attendances.filter((a) => a.classSessionId !== sessionId);
          const now = new Date().toISOString();
          const nextAttendances: SessionAttendance[] = body.items.map((item) => {
            const prior = data.attendances.find(
              (a) =>
                a.classSessionId === sessionId &&
                a.studentTenantAccountId === item.studentTenantAccountId,
            );
            return {
              attendanceId: prior?.attendanceId ?? newAttendanceId(sessionId, item.studentTenantAccountId),
              classSessionId: sessionId,
              studentTenantAccountId: item.studentTenantAccountId,
              status: item.status,
              notes: item.notes ?? null,
              isGuest: prior?.isGuest ?? false,
              checkedInAt: prior?.checkedInAt ?? now,
              checkedInByTenantAccountId:
                prior?.checkedInByTenantAccountId ?? data.currentMemberId,
              createdAt: prior?.createdAt ?? now,
              updatedAt: now,
            };
          });
          return {
            next: {
              ...data,
              attendances: [...existing, ...nextAttendances],
            },
            result: nextAttendances as readonly SessionAttendance[],
          };
        },
      };
    },
  ),
  route(
    'PATCH',
    /^\/api\/tenants\/([^/]+)\/class-sessions\/([^/]+)\/attendance\/([^/]+)$/,
    (ctx) => {
      const sessionId = decodeURIComponent(ctx.match[2]);
      const studentId = decodeURIComponent(ctx.match[3]);
      const body = ctx.body as UpdateAttendanceRequest;
      return {
        mutate: (data: DemoStoreData) => {
          const now = new Date().toISOString();
          let updated: SessionAttendance | null = null;
          const attendances = data.attendances.map((a) => {
            if (a.classSessionId === sessionId && a.studentTenantAccountId === studentId) {
              updated = { ...a, status: body.status, notes: body.notes ?? null, updatedAt: now };
              return updated;
            }
            return a;
          });
          if (!updated) {
            updated = {
              attendanceId: newAttendanceId(sessionId, studentId),
              classSessionId: sessionId,
              studentTenantAccountId: studentId,
              status: body.status,
              notes: body.notes ?? null,
              isGuest: false,
              checkedInAt: now,
              checkedInByTenantAccountId: data.currentMemberId,
              createdAt: now,
              updatedAt: now,
            };
            attendances.push(updated);
          }
          return { next: { ...data, attendances }, result: updated };
        },
      };
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/class-sessions\/([^/]+)\/students$/,
    (ctx) => {
      const sessionId = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as AddStudentToSessionRequest;
      return {
        mutate: (data: DemoStoreData) => {
          const session = data.sessions.find((s) => s.sessionId === sessionId);
          const groupId = session?.groupId ?? data.groups[0]?.groupId ?? 'grp-001';
          const now = new Date().toISOString();
          const firstName = body.firstName?.trim() ?? '';
          const lastName = body.lastName?.trim() ?? '';
          const newStudent: DemoMember = {
            id: newMemberId(data),
            email: body.email ?? null,
            firstName,
            lastName,
            phone: body.phone ?? null,
            roles: ['Student'] as readonly TenantAccountRole[],
            status: 'Active',
            isClaimed: false,
            pending: true,
            createdAt: now,
            weightKg: null,
            heightCm: null,
            birthdate: null,
            gender: null,
            profilePictureUrl: null,
          };
          const newEnrollment: GroupEnrollment = {
            enrollmentId: newEnrollmentId(data),
            tenantId: data.tenant.tenantId,
            groupId,
            studentTenantAccountId: newStudent.id,
            status: 'Active',
            startDate: now.slice(0, 10),
            endDate: null,
            notes: null,
            monthlyFeeOverride: null,
            createdAt: now,
            updatedAt: null,
          };
          const newAttendance: SessionAttendance = {
            attendanceId: newAttendanceId(sessionId, newStudent.id),
            classSessionId: sessionId,
            studentTenantAccountId: newStudent.id,
            status: body.attendanceStatus ?? 'Present',
            notes: body.notes ?? null,
            isGuest: true,
            checkedInAt: now,
            checkedInByTenantAccountId: data.currentMemberId,
            createdAt: now,
            updatedAt: null,
          };
          const result: AddStudentToSessionResponse = {
            student: {
              id: newStudent.id,
              email: newStudent.email,
              firstName: newStudent.firstName || null,
              lastName: newStudent.lastName || null,
              fullName: memberFullName(newStudent),
              phone: newStudent.phone,
              roles: newStudent.roles,
              status: newStudent.status,
              isClaimed: newStudent.isClaimed,
              pending: newStudent.pending,
              createdAt: newStudent.createdAt,
              invitationExpiresAt: null,
              title: null,
            },
            enrollment: newEnrollment,
            attendance: newAttendance,
            invitationUrl: null,
            invitationExpiresAt: null,
          };
          return {
            next: {
              ...data,
              members: [...data.members, newStudent],
              enrollments: [...data.enrollments, newEnrollment],
              attendances: [...data.attendances, newAttendance],
            },
            result,
          };
        },
      };
    },
  ),
];
